/**
 * useEnrichmentProgress Hook
 *
 * Hook that manages the floating progress tracker rendering.
 * Call once at app root level (AppShell) to enable progress tracking
 * for enrichment operations across the entire app.
 *
 * The actual rendering is done by FloatingProgressTracker component,
 * this hook just ensures it stays mounted and responds to store changes.
 *
 * Pattern follows: useRouteCalculationToast.tsx
 *
 * @example
 * ```tsx
 * // In AppShell.tsx
 * useEnrichmentProgress();
 * ```
 */

import { useEffect, useRef } from 'react';
import { useShallow } from 'zustand/shallow';
import {
  useEnrichmentProgressStore,
  selectHasActiveOperations,
} from '../store/enrichmentProgressStore';
import { logger } from '../utils/logger';

/**
 * Hook that enables enrichment progress tracking.
 * Call this once at the app root level.
 *
 * Features:
 * - Monitors enrichment operations
 * - Auto-dismisses completed operations after timeout
 * - Logs progress events for debugging
 */
export function useEnrichmentProgress(): void {
  const hasActiveOperations = useEnrichmentProgressStore(
    useShallow(selectHasActiveOperations)
  );
  const operations = useEnrichmentProgressStore((state) => state.operations);
  const dismissOperation = useEnrichmentProgressStore((state) => state.dismissOperation);

  // Track completion timeouts for auto-dismiss
  const completionTimeoutsRef = useRef<Record<string, NodeJS.Timeout>>({});

  // Auto-dismiss completed/failed/cancelled operations after 10 seconds
  useEffect(() => {
    const operationList = Object.values(operations);

    for (const op of operationList) {
      const { id, status } = op;

      // Check if operation is in terminal state and doesn't have a timeout yet
      if (
        (status === 'completed' || status === 'failed' || status === 'cancelled') &&
        !completionTimeoutsRef.current[id]
      ) {
        logger.log(`[useEnrichmentProgress] Scheduling auto-dismiss for ${id} in 10s`);

        completionTimeoutsRef.current[id] = setTimeout(() => {
          logger.log(`[useEnrichmentProgress] Auto-dismissing ${id}`);
          dismissOperation(id);
          delete completionTimeoutsRef.current[id];
        }, 10000);
      }
    }

    // Cleanup timeouts for operations that were manually dismissed
    const activeIds = new Set(operationList.map((op) => op.id));
    for (const id of Object.keys(completionTimeoutsRef.current)) {
      if (!activeIds.has(id)) {
        clearTimeout(completionTimeoutsRef.current[id]);
        delete completionTimeoutsRef.current[id];
      }
    }
  }, [operations, dismissOperation]);

  // Cleanup all timeouts on unmount
  useEffect(() => {
    return () => {
      for (const timeout of Object.values(completionTimeoutsRef.current)) {
        clearTimeout(timeout);
      }
      completionTimeoutsRef.current = {};
    };
  }, []);

  // Log state changes for debugging
  useEffect(() => {
    if (hasActiveOperations) {
      logger.log('[useEnrichmentProgress] Active operations detected');
    }
  }, [hasActiveOperations]);
}

export default useEnrichmentProgress;
